import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../firebase';

interface User {
  uid: string;
  email: string;
  name: string;
  photoURL?: string;
  tests?: any;
  bookmarks?: any;
}

interface Test {
  id: string | number;
  name: string;
  duration: number;
  questionsCount: number;
  tags?: string[];
  negativeMarks?: number;
  totalMarks: number;
  questions?: any[];
}

interface AppContextType {
  user: User | null;
  loading: boolean;
  routeLoading: boolean;
  theme: string;
  currentTest: Test | null;
  testResults: any;
  examResults: any;
  needsOnboarding: boolean;
  setCurrentTest: (test: Test | null) => void;
  setTestResults: (results: any) => void;
  setRouteLoading: (loading: boolean) => void;
  toggleTheme: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [routeLoading, setRouteLoading] = useState(false);
  const [currentTest, setCurrentTest] = useState<Test | null>(() => {
    const savedTest = sessionStorage.getItem('currentTest');
    return savedTest ? JSON.parse(savedTest) : null;
  });
  const [testResults, setTestResults] = useState<any>(null);
  const [examResults, setExamResults] = useState<any>({});
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
  });
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const setCurrentTestAndPersist = (test: Test | null) => {
    const testString = test ? JSON.stringify(test) : null;
    
    setCurrentTest(prevTest => {
      const prevTestString = prevTest ? JSON.stringify(prevTest) : null;
      if (testString === prevTestString) {
        return prevTest;
      }
      
      if (testString) {
        sessionStorage.setItem('currentTest', testString);
        if (user) {
          sessionStorage.setItem('currentTestUserId', user.uid);
        }
      } else {
        sessionStorage.removeItem('currentTest');
        sessionStorage.removeItem('currentTestUserId');
      }
      
      return test;
    });
  };

  // Theme management
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Firebase auth and data
  useEffect(() => {
    const examsRef = db.ref('exams');
    examsRef.on('value', (snapshot) => setExamResults(snapshot.val() || {}));

    const unsubscribe = auth.onIdTokenChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = db.ref(`users/${firebaseUser.uid}`);
        userRef.on('value', (snapshot) => {
          const dbUser = snapshot.val();
          if (dbUser) {
            setUser({
              ...dbUser,
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              tests: dbUser?.tests || { assigned: {}, completed: {} },
              bookmarks: dbUser?.bookmarks || {},
            });
            setNeedsOnboarding(false);
          } else {
            setUser({ uid: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName });
            setNeedsOnboarding(true);
          }
          setLoading(false);
        });
      } else {
        setUser(null);
        setLoading(false);
        setNeedsOnboarding(false);
      }
    });

    // Set up a timer to refresh the token every 10 minutes
    const tokenRefreshInterval = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true);
      }
    }, 10 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  const value = {
    user,
    loading,
    routeLoading,
    theme,
    currentTest,
    testResults,
    examResults,
    needsOnboarding,
    setCurrentTest: setCurrentTestAndPersist,
    setTestResults,
    setRouteLoading,
    toggleTheme,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};